import React from 'react';
import { Link } from 'react-router-dom';
import A1 from 'assets/A1.svg?react';
import B1 from 'assets/B1.svg?react';
import C1 from 'assets/C1.svg?react';
import UICard from 'components/ui/UICard';
import UIFlex from 'components/ui/UIFlex';
import UIBox from 'components/ui/UIBox';
import UIText from 'components/ui/UIText';
import { fillUrlIds, pagePaths } from 'routes/paths';
import { useAppSelector } from 'app/hooks';
import commonSlice from 'app/store/common.slice';

/**
 * Row for the "Англійська лексика за рівнями" entry. Uses the same row frame
 * as {@link WordSetRow}; only difference is the thumbnail — a fan of three
 * CEFR cards (A1·B1·C1) — to make the level-based set visually distinct.
 *
 * Content uses the same eyebrow / title / description / stat-pill rhythm as
 * the rest of the catalog so this row reads as part of a coherent list, not
 * a special hero.
 */
const ACCENT = '#3B2C93';

const FAN: Array<{
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  rot: number;
  dx: number;
}> = [
  { Icon: A1, rot: -14, dx: -16 },
  { Icon: B1, rot: 0, dx: 0 },
  { Icon: C1, rot: 14, dx: 16 },
];

const hexToRgba = (hex: string, a: number) => {
  const h = hex.replace('#', '');
  return `rgba(${parseInt(h.slice(0, 2), 16)}, ${parseInt(
    h.slice(2, 4),
    16,
  )}, ${parseInt(h.slice(4, 6), 16)}, ${a})`;
};

const StatPill: React.FC<{ n?: number | string; u: string }> = ({ n, u }) => (
  <UIBox
    sx={{
      alignItems: 'baseline',
      bgcolor: 'background.default',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 999,
      display: 'inline-flex',
      fontSize: 13,
      gap: 0.6,
      px: 1.4,
      py: 0.6,
      whiteSpace: 'nowrap',
    }}
  >
    <UIText component={'b'} sx={{ color: 'text.primary', fontWeight: 800 }}>
      {n ?? 0}
    </UIText>
    <UIText component={'span'} color={'primary.subtitle'} sx={{ fontSize: 13 }}>
      {u}
    </UIText>
  </UIBox>
);

const LevelRow: React.FC = () => {
  const data = useAppSelector((store) =>
    commonSlice.getSelectors().getWordSetDataById(store.common, 'level-based'),
  );

  return (
    <Link
      to={fillUrlIds(pagePaths.wordSetCategoryOverview, {
        wordSetType: 'level-based',
      })}
    >
      <UICard
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          p: 0,
          transition: 'border-color .16s ease',
          '&:hover': {
            borderColor: ACCENT,
            bgcolor: hexToRgba(ACCENT, 0.04),
          },
          '&:hover .ws-row-arrow': {
            transform: 'translateX(4px)',
          },
        }}
      >
        <UIFlex
          ai={'center'}
          sx={{
            flexWrap: 'nowrap',
            gap: { xs: 1.75, md: 2.5 },
            p: { xs: '14px', md: '16px 20px' },
          }}
        >
          {/* CEFR fan thumbnail */}
          <UIBox
            sx={{
              bgcolor: hexToRgba(ACCENT, 0.1),
              borderRadius: 2,
              flex: 'none',
              height: { xs: 60, md: 92 },
              overflow: 'hidden',
              position: 'relative',
              width: { xs: 60, md: 92 },
              '& svg': { width: { xs: 24, md: 34 }, height: 'auto' },
            }}
          >
            {FAN.map(({ Icon, rot, dx }, i) => (
              <UIBox
                key={i}
                sx={{
                  filter: 'drop-shadow(0 2px 4px rgba(20,20,20,0.12))',
                  left: '50%',
                  position: 'absolute',
                  top: '50%',
                  transform: `translate(-50%, -50%) translateX(${dx}px) rotate(${rot}deg)`,
                }}
              >
                <Icon />
              </UIBox>
            ))}
          </UIBox>

          {/* Content */}
          <UIBox sx={{ flex: 1, minWidth: 0 }}>
            <UIText
              sx={{
                color: 'text.primary',
                fontSize: { xs: 10, md: 11 },
                fontWeight: 800,
                letterSpacing: '0.09em',
                mb: 0.5,
                textTransform: 'uppercase',
              }}
            >
              {'English Level-Based Vocabulary'}
            </UIText>
            <UIText
              variant={'h3'}
              sx={{ color: ACCENT, fontSize: { xs: 17, md: 21 }, mb: 0.6 }}
            >
              {'Англійська лексика за рівнями'}
            </UIText>
            <UIText
              color={'primary.subtitle'}
              sx={{
                display: '-webkit-box',
                fontSize: { xs: 13, md: 14 },
                mb: 1.25,
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: { xs: 2, md: 1 },
              }}
            >
              {
                'Лексика згрупована за CEFR-рівнями: від першого знайомства (A1) до близького до носія (C2).'
              }
            </UIText>
            <UIFlex jc={'flex-start'} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <StatPill n={data?.categoryCount} u={'рівнів'} />
              <StatPill n={data?.subCategoryCount} u={'уроків'} />
              <StatPill n={data?.wordsCount} u={'слів'} />
            </UIFlex>
          </UIBox>

          <UIBox
            sx={{
              alignItems: 'center',
              color: ACCENT,
              display: { xs: 'none', sm: 'inline-flex' },
              flex: 'none',
              fontSize: 15,
              fontWeight: 600,
              gap: 0.5,
              pr: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            {'Відкрити'}
            <UIBox
              className={'ws-row-arrow'}
              component={'span'}
              sx={{
                display: 'inline-block',
                transition: 'transform .25s cubic-bezier(0.22,1,0.36,1)',
              }}
            >
              {'→'}
            </UIBox>
          </UIBox>
        </UIFlex>
      </UICard>
    </Link>
  );
};

export default LevelRow;
